/**
 * Verification start page.
 *
 * Shows project branding, available case types, and a "Start Verification"
 * button that creates a case via `POST /v1/applicant/cases` then navigates
 * to the document upload step.
 *
 * @module pages/StartPage
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { applicant } from '@noxverify/api-contracts';
import type { ApiClientError } from '@noxverify/api-client';
import type { ApplicantContext } from '../modules/context/context-loader';
import { getAvailableCaseTypes } from '../modules/onboarding/start-page';
import { getApiClient } from '../lib/api';
import { StepIndicator } from '../components/StepIndicator';
import { Footer } from '../components/Footer';

// Human-readable labels for case types.
const CASE_TYPE_LABELS: Record<applicant.CaseType, string> = {
  kyc_individual: 'KYC Individual',
  kyb_entity: 'KYB Business',
  kym_merchant: 'KYM Merchant',
};

const CASE_TYPE_DESCRIPTIONS: Record<applicant.CaseType, string> = {
  kyc_individual: 'Verify your identity with a government-issued ID and selfie.',
  kyb_entity: 'Verify your business with company registration documents.',
  kym_merchant: 'Verify your merchant account credentials.',
};

interface StartPageProps {
  context: ApplicantContext;
}

export function StartPage({ context }: StartPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const [selectedType, setSelectedType] = useState<applicant.CaseType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const caseTypes = getAvailableCaseTypes(context.workflowTemplateId);

  // Auto-select if only one type available.
  const effectiveSelection = caseTypes.length === 1 ? caseTypes[0] : selectedType;

  const handleStart = useCallback(async () => {
    const caseType = effectiveSelection;
    if (!caseType) return;

    setLoading(true);
    setError(null);

    try {
      const client = getApiClient();
      const response = await client.applicant.createCase({ caseType });
      const { id: caseId, submissionSetId } = response.data;

      // The backend creates the initial submission set as part of case
      // creation, so no separate createSubmissionSet call is needed.
      // Route to the correct flow based on case type.
      switch (caseType) {
        case 'kyb_entity':
          navigate(`/case/${caseId}/business-verification?ss=${submissionSetId}`);
          break;
        case 'kym_merchant':
          navigate(`/case/${caseId}/merchant-onboarding?ss=${submissionSetId}`);
          break;
        case 'kyc_individual':
        default:
          navigate(`/case/${caseId}/details?ss=${submissionSetId}`);
          break;
      }
    } catch (err: unknown) {
      const apiErr = err as ApiClientError;
      setError(
        apiErr.message ??
          'Failed to create verification case. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, [effectiveSelection, navigate]);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Header — hairline rule, logo + project name */}
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-5 py-5">
          {context.branding.logoUrl && (
            <img
              src={context.branding.logoUrl}
              alt={`${context.projectSlug} logo`}
              className="h-8"
            />
          )}
          <h1
            className="text-base font-semibold tracking-tight"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {t('start.title')}
          </h1>
        </div>
      </header>

      {/* Step indicator */}
      <div className="mx-auto w-full max-w-2xl px-5">
        <StepIndicator currentStep="start" />
      </div>

      {/* Main content — single column, generous whitespace */}
      <main className="flex-1">
        <div className="mx-auto max-w-2xl px-5 pb-16 pt-6">
          {/* Editorial eyebrow + hairline */}
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">
            {t('start.title')}
          </p>
          <div className="mt-3 h-px w-10 bg-primary" aria-hidden="true" />

          {/* Welcome headline — IBM Plex Sans display weight */}
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-[-0.02em] text-ink sm:text-5xl">
            {t('start.welcome')}
          </h2>
          <p className="mt-4 max-w-prose text-lg leading-relaxed text-ink-muted">
            {t('start.intro')}
          </p>

          <div className="mt-10 space-y-8">
            {/* Case type selection */}
            {caseTypes.length > 1 && (
              <fieldset>
                <legend className="mb-3 font-mono text-xs uppercase tracking-[0.1em] text-ink-muted">
                  {t('start.selectType')}
                </legend>
                <div className="space-y-3">
                  {caseTypes.map((ct) => (
                    <label
                      key={ct}
                      className={[
                        'flex cursor-pointer items-start gap-3 rounded-lg border bg-surface-raised p-5 transition-colors duration-150',
                        selectedType === ct
                          ? 'border-[var(--nv-primary-color)] bg-primary-tint'
                          : 'border-hairline hover:border-ink/30',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        name="caseType"
                        value={ct}
                        checked={selectedType === ct}
                        onChange={() => setSelectedType(ct)}
                        className="mt-1 h-4 w-4 accent-[var(--nv-primary-color)]"
                      />
                      <div>
                        <span className="font-semibold text-ink">
                          {CASE_TYPE_LABELS[ct]}
                        </span>
                        <p className="mt-1 text-sm leading-relaxed text-ink-muted">
                          {CASE_TYPE_DESCRIPTIONS[ct]}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {/* Single case type display */}
            {caseTypes.length === 1 && (
              <div className="rounded-lg border border-hairline bg-surface-raised p-5">
                <p className="font-semibold text-ink">
                  {CASE_TYPE_LABELS[caseTypes[0]]}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-ink-muted">
                  {CASE_TYPE_DESCRIPTIONS[caseTypes[0]]}
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <div
                role="alert"
                className="rounded-md border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
              >
                {error}
              </div>
            )}

            {/* Start button — full-width primary primary */}
            <button
              type="button"
              onClick={handleStart}
              disabled={loading || !effectiveSelection}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md px-8 py-3.5 text-base font-semibold text-surface transition-colors duration-150 hover:brightness-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nv-primary-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              style={{
                backgroundColor: 'var(--nv-primary-color)',
              }}
            >
              {loading && (
                <span
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-surface border-t-transparent"
                  aria-hidden="true"
                />
              )}
              {loading ? t('start.creating') : t('start.startButton')}
            </button>
          </div>
        </div>
      </main>

      <Footer branding={context.branding} />
    </div>
  );
}
